import React, { useEffect, useMemo, useState } from "react";
import {
  Search,
  SlidersHorizontal,
  X,
  ArrowUpDown,
  LayoutList,
  Layers,
  ChevronDown,
  Tag,
  MapPin,
  CalendarRange,
  UserRound,
  Activity,
} from "lucide-react";
import type { Job, StockRegion, User } from "../types";
import { jobMatchesCategory, jobMatchesDossierTypeFilter } from "../utils/jobCategories";
import type { DossierTypeFilter, JobCategory } from "../utils/jobCategories";
import { DOSSIER_TYPE_LABELS } from "../utils/dossierLabels";
import { getJobDisplayStatus } from "../utils/jobStatus";

export type SortKey = "date" | "createdAt" | "clientName" | "status";
export type SortDir = "asc" | "desc";
export type GroupBy = "none" | "status";

export interface JobsFiltersState {
  search: string;
  status: Job["status"] | "";
  /** Catégorie hub (prioritaire sur status quand définie et !== "all"). */
  category: JobCategory | "";
  dossierType: DossierTypeFilter | "";
  stockRegionId: string;
  installerId: string;
  dateFrom: string;
  dateTo: string;
  sortKey: SortKey;
  sortDir: SortDir;
  groupBy: GroupBy;
}

const DEFAULT_STATE: JobsFiltersState = {
  search: "",
  status: "",
  category: "",
  dossierType: "",
  stockRegionId: "",
  installerId: "",
  dateFrom: "",
  dateTo: "",
  sortKey: "date",
  sortDir: "desc",
  groupBy: "none",
};

const STATUS_LABELS: Record<Job["status"], string> = {
  pending: "Sans date",
  scheduled: "À faire",
  in_progress: "En cours",
  completed: "Terminé",
  cancelled: "Annulé",
};

const STATUS_ORDER: Job["status"][] = [
  "in_progress",
  "scheduled",
  "pending",
  "completed",
  "cancelled",
];

function loadState(storageKey: string): JobsFiltersState {
  if (typeof window === "undefined") return DEFAULT_STATE;
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return DEFAULT_STATE;
    const parsed = JSON.parse(raw) as Partial<JobsFiltersState>;
    return { ...DEFAULT_STATE, ...parsed };
  } catch {
    return DEFAULT_STATE;
  }
}

function saveState(storageKey: string, state: JobsFiltersState) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(state));
  } catch {
    /* ignore */
  }
}

export interface UseJobsFiltersOptions {
  storageKey: string;
  defaults?: Partial<JobsFiltersState>;
}

export interface UseJobsFiltersResult {
  state: JobsFiltersState;
  setField: <K extends keyof JobsFiltersState>(key: K, value: JobsFiltersState[K]) => void;
  reset: () => void;
  filtered: Job[];
  /** Groupes par statut (ordre : in_progress, scheduled, pending, completed, cancelled). */
  grouped: Array<{ status: Job["status"]; jobs: Job[] }>;
  activeCount: number;
}

export function useJobsFilters(
  jobs: Job[],
  options: UseJobsFiltersOptions
): UseJobsFiltersResult {
  const { storageKey, defaults } = options;
  const [initial] = useState<JobsFiltersState>(() => ({
    ...DEFAULT_STATE,
    ...defaults,
    ...loadState(storageKey),
  }));
  const [state, setState] = useState<JobsFiltersState>(initial);

  useEffect(() => {
    saveState(storageKey, state);
  }, [storageKey, state]);

  const setField: UseJobsFiltersResult["setField"] = (key, value) => {
    setState((prev) => ({ ...prev, [key]: value }));
  };

  const reset = () => setState({ ...DEFAULT_STATE, ...defaults });

  const filtered = useMemo(() => {
    const q = state.search.trim().toLowerCase();
    let out = jobs.filter((j) => {
      if (state.category && state.category !== "all") {
        if (!jobMatchesCategory(j, state.category)) return false;
      } else if (state.status && getJobDisplayStatus(j) !== state.status) {
        return false;
      }
      if (state.dossierType && !jobMatchesDossierTypeFilter(j, state.dossierType)) return false;
      if (state.stockRegionId && j.stockRegionId !== state.stockRegionId) return false;
      if (state.installerId && j.installerId !== state.installerId) return false;
      if (state.dateFrom && (!j.date || j.date < state.dateFrom)) return false;
      if (state.dateTo && (!j.date || j.date > state.dateTo)) return false;
      if (q) {
        const hay = [
          j.clientName,
          j.clientPhone,
          j.clientAddress,
          j.id,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });

    const dir = state.sortDir === "asc" ? 1 : -1;
    out = [...out].sort((a, b) => {
      let av: string;
      let bv: string;
      switch (state.sortKey) {
        case "clientName":
          av = (a.clientName || "").toLowerCase();
          bv = (b.clientName || "").toLowerCase();
          break;
        case "status":
          av = getJobDisplayStatus(a);
          bv = getJobDisplayStatus(b);
          break;
        case "createdAt":
          av = a.createdAt || "";
          bv = b.createdAt || "";
          break;
        case "date":
        default:
          av = a.date || "";
          bv = b.date || "";
          break;
      }
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });

    return out;
  }, [jobs, state]);

  const grouped = useMemo(() => {
    const buckets = new Map<Job["status"], Job[]>();
    for (const s of STATUS_ORDER) buckets.set(s, []);
    for (const j of filtered) {
      const displayStatus = getJobDisplayStatus(j);
      const arr = buckets.get(displayStatus);
      if (arr) arr.push(j);
    }
    return STATUS_ORDER.map((status) => ({ status, jobs: buckets.get(status) ?? [] })).filter(
      (g) => g.jobs.length > 0
    );
  }, [filtered]);

  const activeCount = useMemo(() => {
    let n = 0;
    if (state.search.trim()) n++;
    if (state.status) n++;
    if (state.dossierType) n++;
    if (state.stockRegionId) n++;
    if (state.installerId) n++;
    if (state.dateFrom) n++;
    if (state.dateTo) n++;
    return n;
  }, [state]);

  return { state, setField, reset, filtered, grouped, activeCount };
}

export interface JobsFiltersBarProps {
  filters: UseJobsFiltersResult;
  stockRegions?: StockRegion[];
  installers?: User[];
  /** "compact" = mobile (replié derrière un bouton), "expanded" = desktop (toujours visible). */
  layout?: "compact" | "expanded";
  /** Couleur d'accent (tailwind : "indigo" pour partenaire, "blue" pour poseur). */
  accent?: "indigo" | "blue";
  /** Afficher le filtre par poseur (utile côté partenaire). */
  showInstallerFilter?: boolean;
  /** Afficher le toggle de groupement par statut. */
  showGroupToggle?: boolean;
  /** Afficher les pills de filtre par statut (désactivé quand hub catégories actif). */
  showStatusPills?: boolean;
  /** Nombre total de dossiers (avant filtre) pour afficher le compteur. */
  totalCount: number;
}

export function JobsFiltersBar({
  filters,
  stockRegions = [],
  installers = [],
  layout = "expanded",
  accent = "indigo",
  showInstallerFilter = false,
  showGroupToggle = true,
  showStatusPills = true,
  totalCount,
}: JobsFiltersBarProps) {
  const { state, setField, reset, filtered, activeCount } = filters;
  const [open, setOpen] = useState(layout === "expanded");

  useEffect(() => {
    if (layout === "expanded") setOpen(true);
  }, [layout]);

  const accentClasses = {
    ring: accent === "blue" ? "focus:ring-blue-500/30" : "focus:ring-indigo-500/30",
    borderFocus: accent === "blue" ? "focus:border-blue-500/60" : "focus:border-indigo-500/60",
    badgeBg: accent === "blue" ? "bg-blue-600" : "bg-indigo-600",
    pillActiveBg: accent === "blue"
      ? "bg-blue-600 text-white border-blue-600 shadow-sm shadow-blue-500/20"
      : "bg-indigo-600 text-white border-indigo-600 shadow-sm shadow-indigo-500/20",
    chipBg: accent === "blue"
      ? "bg-blue-50 text-blue-700 border-blue-200/60 dark:bg-blue-500/10 dark:text-blue-300 dark:border-blue-500/20"
      : "bg-indigo-50 text-indigo-700 border-indigo-200/60 dark:bg-indigo-500/10 dark:text-indigo-300 dark:border-indigo-500/20",
    iconAccent: accent === "blue" ? "text-blue-500" : "text-indigo-500",
  };

  // Tailles standardisées (alignement visuel strict)
  const H_FIELD = "h-10"; // inputs + selects du corps
  const H_CONTROL = "h-9"; // pills statut + contrôles du footer
  const H_CHIP = "h-7"; // chips filtres actifs
  const RADIUS = "rounded-xl";
  const RADIUS_CTRL = "rounded-lg";

  const inputCls = `w-full ${H_FIELD} px-3 bg-white dark:bg-slate-800/60 border border-gray-200 dark:border-white/10 ${RADIUS} text-[13px] text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 ${accentClasses.ring} ${accentClasses.borderFocus} focus:ring-2 outline-none transition-all`;
  const selectCls = `${inputCls} appearance-none pr-9 cursor-pointer`;

  const Field = ({
    icon,
    label,
    children,
  }: {
    icon: React.ReactNode;
    label: string;
    children: React.ReactNode;
  }) => (
    <label className="block">
      <span className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-slate-400 mb-1.5">
        <span className={accentClasses.iconAccent}>{icon}</span>
        {label}
      </span>
      {children}
    </label>
  );

  const SelectWrapper = ({ children }: { children: React.ReactNode }) => (
    <div className="relative">
      {children}
      <ChevronDown
        size={14}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-slate-500 pointer-events-none"
      />
    </div>
  );

  // Chips actifs : liste humaine des filtres en cours
  const activeChips: Array<{ key: string; label: string; clear: () => void }> = [];
  if (state.search.trim())
    activeChips.push({
      key: "search",
      label: `« ${state.search.trim()} »`,
      clear: () => setField("search", ""),
    });
  if (state.status)
    activeChips.push({
      key: "status",
      label: STATUS_LABELS[state.status as Job["status"]],
      clear: () => setField("status", ""),
    });
  if (state.dossierType)
    activeChips.push({
      key: "dossierType",
      label:
        state.dossierType === "__none__"
          ? "Sans type"
          : DOSSIER_TYPE_LABELS[state.dossierType as NonNullable<Job["dossierType"]>] ??
            state.dossierType,
      clear: () => setField("dossierType", ""),
    });
  if (state.stockRegionId) {
    const r = stockRegions.find((x) => x.id === state.stockRegionId);
    activeChips.push({
      key: "region",
      label: r?.name ?? "Région",
      clear: () => setField("stockRegionId", ""),
    });
  }
  if (state.installerId) {
    const i = installers.find((x) => x.id === state.installerId);
    activeChips.push({
      key: "installer",
      label: i?.name ?? "Poseur",
      clear: () => setField("installerId", ""),
    });
  }
  if (state.dateFrom || state.dateTo) {
    const fmt = (d: string) =>
      d ? new Date(d).toLocaleDateString("fr-FR", { day: "2-digit", month: "short" }) : "…";
    activeChips.push({
      key: "dates",
      label: `${fmt(state.dateFrom)} → ${fmt(state.dateTo)}`,
      clear: () => {
        setField("dateFrom", "");
        setField("dateTo", "");
      },
    });
  }

  const panel = (
    <div className="bg-white dark:bg-slate-900/70 border border-gray-200/80 dark:border-white/10 rounded-2xl shadow-sm shadow-gray-200/30 dark:shadow-none overflow-hidden backdrop-blur-sm">
      {/* Barre de recherche + compteur */}
      <div className="px-4 pt-4 pb-3.5">
        <div className="relative">
          <Search
            size={16}
            className={`absolute left-3.5 top-1/2 -translate-y-1/2 ${accentClasses.iconAccent} pointer-events-none`}
          />
          <input
            type="text"
            value={state.search}
            onChange={(e) => setField("search", e.target.value)}
            placeholder="Rechercher un client, téléphone, adresse…"
            className={`${inputCls} pl-10 pr-9`}
          />
          {state.search && (
            <button
              type="button"
              onClick={() => setField("search", "")}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-slate-700 dark:hover:text-slate-200 transition-colors"
              aria-label="Effacer la recherche"
            >
              <X size={14} />
            </button>
          )}
        </div>
      </div>

      {/* Segment control : statuts rapides (répartis sur toute la largeur) */}
      {showStatusPills && (
        <div className="px-4 pb-3.5">
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-1.5">
            <button
              type="button"
              onClick={() => setField("status", "")}
              className={`inline-flex items-center justify-center px-2 ${H_CONTROL} ${RADIUS_CTRL} text-[12px] font-medium border transition-all ${
                !state.status
                  ? accentClasses.pillActiveBg
                  : "bg-white dark:bg-slate-800/60 border-gray-200 dark:border-white/10 text-gray-600 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-700/60"
              }`}
            >
              Tous
            </button>
            {STATUS_ORDER.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setField("status", s)}
                className={`inline-flex items-center justify-center px-2 ${H_CONTROL} ${RADIUS_CTRL} text-[12px] font-medium border transition-all ${
                  state.status === s
                    ? accentClasses.pillActiveBg
                    : "bg-white dark:bg-slate-800/60 border-gray-200 dark:border-white/10 text-gray-600 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-700/60"
                }`}
              >
                {STATUS_LABELS[s]}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Filtres détaillés */}
      <div className="px-4 py-3.5 border-t border-gray-100 dark:border-white/5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <Field icon={<Tag size={13} />} label="Type de dossier">
          <SelectWrapper>
            <select
              value={state.dossierType}
              onChange={(e) =>
                setField("dossierType", e.target.value as JobsFiltersState["dossierType"])
              }
              className={selectCls}
            >
              <option value="">Tous les types</option>
              {(Object.keys(DOSSIER_TYPE_LABELS) as Array<NonNullable<Job["dossierType"]>>).map(
                (k) => (
                  <option key={k} value={k}>
                    {DOSSIER_TYPE_LABELS[k]}
                  </option>
                )
              )}
              <option value="__none__">Sans type</option>
            </select>
          </SelectWrapper>
        </Field>

        {stockRegions.length > 0 && (
          <Field icon={<MapPin size={13} />} label="Région">
            <SelectWrapper>
              <select
                value={state.stockRegionId}
                onChange={(e) => setField("stockRegionId", e.target.value)}
                className={selectCls}
              >
                <option value="">Toutes les régions</option>
                {stockRegions.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name}
                  </option>
                ))}
              </select>
            </SelectWrapper>
          </Field>
        )}

        {showInstallerFilter && installers.length > 0 && (
          <Field icon={<UserRound size={13} />} label="Poseur">
            <SelectWrapper>
              <select
                value={state.installerId}
                onChange={(e) => setField("installerId", e.target.value)}
                className={selectCls}
              >
                <option value="">Tous les poseurs</option>
                {installers.map((i) => (
                  <option key={i.id} value={i.id}>
                    {i.name}
                  </option>
                ))}
              </select>
            </SelectWrapper>
          </Field>
        )}

        <Field icon={<CalendarRange size={13} />} label="Période">
          <div className="flex items-center gap-1.5">
            <input
              type="date"
              value={state.dateFrom}
              onChange={(e) => setField("dateFrom", e.target.value)}
              className={inputCls}
              aria-label="Date de début"
            />
            <span className="text-gray-400 dark:text-slate-500 text-xs">→</span>
            <input
              type="date"
              value={state.dateTo}
              onChange={(e) => setField("dateTo", e.target.value)}
              className={inputCls}
              aria-label="Date de fin"
            />
          </div>
        </Field>
      </div>

      {/* Footer : tri + groupement + compteur + reset */}
      <div className="px-4 py-3.5 border-t border-gray-100 dark:border-white/5 bg-gray-50/50 dark:bg-slate-900/30 flex flex-wrap items-center gap-2">
        <div className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-slate-400 mr-0.5">
          <ArrowUpDown size={12} />
          Trier
        </div>
        <SelectWrapper>
          <select
            value={state.sortKey}
            onChange={(e) => setField("sortKey", e.target.value as SortKey)}
            className={`${H_CONTROL} pl-3 pr-8 bg-white dark:bg-slate-800/60 border border-gray-200 dark:border-white/10 ${RADIUS_CTRL} text-[12px] text-gray-900 dark:text-white outline-none appearance-none cursor-pointer hover:bg-gray-50 dark:hover:bg-slate-700/60 transition-colors`}
          >
            <option value="date">Date d&apos;intervention</option>
            <option value="createdAt">Date de création</option>
            <option value="clientName">Nom client</option>
            <option value="status">Statut</option>
          </select>
        </SelectWrapper>
        <button
          type="button"
          onClick={() => setField("sortDir", state.sortDir === "asc" ? "desc" : "asc")}
          className={`${H_CONTROL} w-9 inline-flex items-center justify-center bg-white dark:bg-slate-800/60 border border-gray-200 dark:border-white/10 ${RADIUS_CTRL} text-gray-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-700/60 transition-colors`}
          title={state.sortDir === "asc" ? "Ascendant" : "Descendant"}
          aria-label={state.sortDir === "asc" ? "Tri ascendant" : "Tri descendant"}
        >
          <span className="text-sm leading-none">{state.sortDir === "asc" ? "↑" : "↓"}</span>
        </button>

        {showGroupToggle && (
          <div className={`inline-flex ${RADIUS_CTRL} border border-gray-200 dark:border-white/10 overflow-hidden ${H_CONTROL} bg-white dark:bg-slate-800/60 p-0.5`}>
            <button
              type="button"
              onClick={() => setField("groupBy", "none")}
              className={`inline-flex items-center gap-1.5 px-2.5 h-full rounded-md text-[12px] font-medium transition-colors ${
                state.groupBy === "none"
                  ? accentClasses.pillActiveBg
                  : "text-gray-600 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-700/60"
              }`}
              title="Liste plate"
            >
              <LayoutList size={13} /> Liste
            </button>
            <button
              type="button"
              onClick={() => setField("groupBy", "status")}
              className={`inline-flex items-center gap-1.5 px-2.5 h-full rounded-md text-[12px] font-medium transition-colors ${
                state.groupBy === "status"
                  ? accentClasses.pillActiveBg
                  : "text-gray-600 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-700/60"
              }`}
              title="Grouper par statut"
            >
              <Layers size={13} /> Groupés
            </button>
          </div>
        )}

        <div className="ml-auto inline-flex items-center gap-2">
          <span className="inline-flex items-center gap-1.5 text-[12px] text-gray-500 dark:text-slate-400 font-medium tabular-nums">
            <Activity size={12} className={accentClasses.iconAccent} />
            {filtered.length}
            <span className="text-gray-300 dark:text-slate-600">/</span>
            {totalCount}
          </span>
          {activeCount > 0 && (
            <button
              type="button"
              onClick={reset}
              className={`${H_CONTROL} px-2.5 inline-flex items-center gap-1.5 ${RADIUS_CTRL} text-[12px] font-medium text-gray-600 dark:text-slate-300 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors`}
            >
              <X size={13} /> Effacer
            </button>
          )}
        </div>
      </div>

      {/* Chips actifs */}
      {activeChips.length > 0 && (
        <div className="px-4 py-3.5 border-t border-gray-100 dark:border-white/5 flex flex-wrap gap-1.5">
          {activeChips.map((chip) => (
            <span
              key={chip.key}
              className={`inline-flex items-center gap-1 pl-2.5 pr-1 ${H_CHIP} rounded-full text-[11px] font-medium border ${accentClasses.chipBg}`}
            >
              {chip.label}
              <button
                type="button"
                onClick={chip.clear}
                className="inline-flex items-center justify-center w-5 h-5 rounded-full hover:bg-white/60 dark:hover:bg-slate-800/60 transition-colors"
                aria-label={`Retirer ${chip.label}`}
              >
                <X size={11} />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );

  if (layout === "expanded") {
    return panel;
  }

  // layout compact (mobile) : header sticky avec recherche inline + bouton filtres
  return (
    <div className="space-y-2.5">
      {/* Barre recherche inline + bouton filtres */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search
            size={16}
            className={`absolute left-3 top-1/2 -translate-y-1/2 ${accentClasses.iconAccent} pointer-events-none`}
          />
          <input
            type="text"
            value={state.search}
            onChange={(e) => setField("search", e.target.value)}
            placeholder="Rechercher…"
            className={`w-full ${H_FIELD} pl-9 pr-9 bg-white dark:bg-slate-800/80 border border-gray-200 dark:border-white/10 ${RADIUS} text-[14px] text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 ${accentClasses.ring} ${accentClasses.borderFocus} focus:ring-2 outline-none transition-all`}
          />
          {state.search && (
            <button
              type="button"
              onClick={() => setField("search", "")}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-slate-200 transition-colors"
              aria-label="Effacer la recherche"
            >
              <X size={14} />
            </button>
          )}
        </div>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className={`relative ${H_FIELD} w-10 inline-flex items-center justify-center ${RADIUS} border transition-all ${
            open
              ? accentClasses.pillActiveBg
              : activeCount > 0
              ? accentClasses.chipBg
              : "bg-white dark:bg-slate-800/80 border-gray-200 dark:border-white/10 text-gray-600 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-700/60"
          }`}
          aria-label="Filtres et tri"
          aria-expanded={open}
        >
          <SlidersHorizontal size={17} />
          {activeCount > 0 && !open && (
            <span
              className={`absolute -top-1 -right-1 inline-flex items-center justify-center min-w-[18px] h-[18px] text-[10px] font-bold text-white ${accentClasses.badgeBg} rounded-full px-1 ring-2 ring-white dark:ring-slate-900`}
            >
              {activeCount}
            </span>
          )}
        </button>
      </div>

      {/* Chips actifs horizontaux (visibles même panneau replié) */}
      {!open && activeChips.length > 0 && (
        <div className="flex gap-1.5 overflow-x-auto no-scrollbar -mx-1 px-1 pb-0.5">
          {activeChips.map((chip) => (
            <span
              key={chip.key}
              className={`shrink-0 inline-flex items-center gap-1 pl-2.5 pr-1 ${H_CHIP} rounded-full text-[11px] font-medium border ${accentClasses.chipBg}`}
            >
              {chip.label}
              <button
                type="button"
                onClick={chip.clear}
                className="inline-flex items-center justify-center w-5 h-5 rounded-full hover:bg-white/60 dark:hover:bg-slate-800/60 transition-colors"
                aria-label={`Retirer ${chip.label}`}
              >
                <X size={11} />
              </button>
            </span>
          ))}
          <button
            type="button"
            onClick={reset}
            className="shrink-0 h-7 px-2.5 inline-flex items-center gap-1 rounded-full text-[11px] font-medium text-gray-500 dark:text-slate-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
          >
            <X size={11} /> Tout effacer
          </button>
        </div>
      )}

      {/* Panneau ouvert : segment statuts + filtres + tri */}
      {open && panel}

      {/* Compteur + résumé quand panneau replié */}
      {!open && (
        <div className="flex items-center justify-between px-1 text-[11px] text-gray-500 dark:text-slate-400">
          <span className="inline-flex items-center gap-1.5 font-medium tabular-nums">
            <Activity size={11} className={accentClasses.iconAccent} />
            {filtered.length}
            <span className="text-gray-300 dark:text-slate-600">/</span>
            {totalCount} dossiers
          </span>
          <span className="inline-flex items-center gap-1">
            <ArrowUpDown size={11} />
            {state.sortKey === "date"
              ? "Date interv."
              : state.sortKey === "createdAt"
              ? "Date créa."
              : state.sortKey === "clientName"
              ? "Nom"
              : "Statut"}
            {state.sortDir === "asc" ? " ↑" : " ↓"}
          </span>
        </div>
      )}
    </div>
  );
}

export { STATUS_LABELS, DOSSIER_TYPE_LABELS, STATUS_ORDER };
